import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isNil, size } from "lodash";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CheckRounded,
  CloseRounded,
  EditOutlined,
  EventOutlined,
  KeyboardArrowDown,
  ModeCommentOutlined,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import { useDispatch, useSelector } from "react-redux";

import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { useFileDocGroupAndTypesHook } from "../../hooks/useFileDocGroupAndTypesHook";
import { useCommentUtilsHook } from "../../hooks/utils/useCommentUtilsHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import SelectFileAssignmentInput from "../../components/inputs/SelectFileAssignmentInput";
import LoanFilePreviewModal from "../../components/modals/files/LoanFilePreviewModal";
import CommentThreadPreviewTooltip from "../../components/tooltips/CommentThreadPreviewTooltip";
// Date display goes through the project's Eastern-time formatter — see the
// date-handling convention in CLAUDE.md (always use formatDateTime / getDaysAgo
// so timezone handling is consistent across the app).
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { isDocHidden } from "../../_src_shared/utils/functions/isDocHidden";
import Txt from "../../components/typography/Txt";
import Flx from "../../components/layout/Flx";

/* Slate-leaning palette — shared with the rest of the redesign. */
const INK = "#0f172a";
const INK2 = "#334155";
const INK3 = "#64748b";
const INK4 = "#94a3b8";
const LINE = "#e5e3df";
const LINE2 = "#e6ebf2";
const SURFACE2 = "#f3f5f8";
const GOOD = "#2d6a4a";
const INFO = "#2c5282";
const INFO_BG = "#e6edf5";
const INFO_LINE = "#c7d6e9";

const formatReportDate = (iso) => formatDateTime(iso, "date") || "—";

// Re-append the original extension if the user edited it off the filename, so
// inline renames can't accidentally strip the file type.
const preserveExtension = (oldName, nextRaw) => {
  const next = (nextRaw || "").trim();
  if (!next) return null;
  const extMatch = (oldName || "").match(/\.([^.]+)$/);
  if (!extMatch) return next;
  const ext = extMatch[1];
  if (next.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) return next;
  return `${next.replace(/\.[^/.]+$/, "")}.${ext}`;
};

/* ═══════════════════════════════════════════════════════════
   FILENAME CELL — click the name to preview (hover underlines);
   hover the cell to reveal a rename (pencil) button that swaps
   the name for an inline text field. Mirrors the Document
   Manager's filename cell.
   ═══════════════════════════════════════════════════════════ */

const FilenameCell = (params) => {
  const dispatch = useDispatch();
  const { loanId, onPreview } = params?.context || {};
  const { updateLoanDocMetadata, loading } = useLoanFilesHook();
  const { getFileAssociatedComments } = useCommentUtilsHook();
  // One subscription per cell — these tables hold only a handful of rows
  // (a borrower / property's files), so the cost is negligible. Comments
  // associated with this specific document drive the comment badge.
  const comments = useSelector((s) => s.loanDrilldown?.comments);
  const doc = params?.data;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const docComments = useMemo(
    () => (doc?._id ? getFileAssociatedComments(comments, doc._id) : []),
    [comments, doc?._id, getFileAssociatedComments],
  );

  const name = doc?.file_display_name || doc?.fileName || "Untitled";
  const commentCount = docComments.length;
  const reportEffectiveDate = doc?.reportEffectiveDate;
  const reportDurationDays = doc?.reportGuidelineExpirationDays;
  const hasReportInfo = !!(reportEffectiveDate || reportDurationDays);

  if (!doc) return null;

  const startEdit = (e) => {
    e?.stopPropagation();
    setValue(name);
    setEditing(true);
  };

  const commit = () => {
    const nextName = preserveExtension(name, value);
    if (!nextName || nextName === name || !doc?._id) {
      setEditing(false);
      return;
    }
    updateLoanDocMetadata({
      loanId,
      data: [{ _id: doc._id, file_display_name: nextName }],
      onSuccessFn: (d) => {
        dispatch(loanDrilldownSet(d));
        setEditing(false);
      },
      onFailFn: () => setEditing(false),
    });
  };

  if (editing) {
    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}
      >
        <TextField
          value={value}
          onChange={(e) => setValue(e.target.value)}
          size="small"
          autoFocus
          fullWidth
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditing(false);
          }}
          sx={{
            "& .MuiInputBase-input": { fontSize: 12, py: "4px" },
          }}
        />
        <IconButton
          size="small"
          onClick={commit}
          disabled={loading}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <CheckRounded sx={{ fontSize: 16, color: GOOD }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setEditing(false)}
          disabled={loading}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <CloseRounded sx={{ fontSize: 16, color: INK3 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        width: "100%",
        minWidth: 0,
        "&:hover .file-edit-btn": { opacity: 1 },
      }}
    >
      <Tooltip title="Preview file" placement="top" arrow>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onPreview?.(doc);
          }}
          sx={{
            fontSize: 12,
            color: INK,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {name}
        </Box>
      </Tooltip>

      <Tooltip title="Rename" placement="top" arrow>
        <IconButton
          className="file-edit-btn"
          size="small"
          onClick={startEdit}
          sx={{
            p: 0.25,
            flexShrink: 0,
            opacity: 0,
            transition: "opacity 0.12s",
          }}
        >
          <EditOutlined sx={{ fontSize: 14, color: INK3 }} />
        </IconButton>
      </Tooltip>

      {/* Comment badge — hover shows the thread preview. */}
      {commentCount > 0 && (
        <CommentThreadPreviewTooltip
          loan={{ comments: docComments }}
          comments={docComments}
        >
          <Flx
            ac
            g={0.4}
            sx={{
              flexShrink: 0,
              px: 0.75,
              py: "1px",
              borderRadius: "10px",
              bgcolor: INFO_BG,
              color: INFO,
              border: `1px solid ${INFO_LINE}`,
              cursor: "help",
            }}
          >
            <ModeCommentOutlined sx={{ fontSize: "11px !important" }} />
            <Txt sx={{ fontSize: "10px", fontWeight: 600, color: INFO }}>
              {commentCount}
            </Txt>
          </Flx>
        </CommentThreadPreviewTooltip>
      )}

      {/* Report effective-date chip — display-only. */}
      {hasReportInfo && (
        <Tooltip
          placement="top"
          arrow
          title={
            reportEffectiveDate && reportDurationDays
              ? `Report effective ${formatReportDate(reportEffectiveDate)} · valid ${reportDurationDays} day${reportDurationDays === 1 ? "" : "s"}`
              : reportEffectiveDate
                ? `Report effective ${formatReportDate(reportEffectiveDate)}`
                : `Report valid ${reportDurationDays} day${reportDurationDays === 1 ? "" : "s"}`
          }
        >
          <Flx
            ac
            g={0.4}
            sx={{
              flexShrink: 0,
              px: 0.75,
              py: "1px",
              borderRadius: "10px",
              bgcolor: SURFACE2,
              border: `1px solid ${LINE2}`,
              color: INK2,
            }}
          >
            <EventOutlined sx={{ fontSize: "12px !important", color: INK3 }} />
            <Txt sx={{ fontSize: "10px", color: INK2 }}>
              {reportEffectiveDate
                ? formatReportDate(reportEffectiveDate)
                : "—"}
              {reportDurationDays != null && (
                <Box component="span" sx={{ ml: 0.4, color: INK4 }}>
                  · {reportDurationDays}d
                </Box>
              )}
            </Txt>
          </Flx>
        </Tooltip>
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════
   META CELL — click opens a popover to reassign the file's
   doc type (category + type) or doc group, then saves via
   updateLoanDocMetadata. The edited field is driven by
   `metaField` on the grid context ("docType" | "docGroup").
   ═══════════════════════════════════════════════════════════ */

const InlineMetaCell = (params) => {
  const dispatch = useDispatch();
  const { loanId, metaField, docGroupOptions } = params?.context || {};
  const isDocGroup = metaField === "docGroup";
  const { updateLoanDocMetadata, loading } = useLoanFilesHook();
  const { fileDocGroups } = useFileDocGroupAndTypesHook();
  const [anchor, setAnchor] = useState(null);
  const [pending, setPending] = useState({ docGroup: null, docType: null });
  const doc = params?.data;

  // Seed pending values from the doc each time the popover opens.
  useEffect(() => {
    if (anchor && doc) {
      setPending({ docGroup: doc.docGroup, docType: doc.docType });
    }
  }, [anchor, doc]);

  if (!doc) return null;

  const currentValue = doc[metaField];
  const isMissing = isNil(currentValue) || currentValue === "";
  const isDirty = isDocGroup
    ? pending.docGroup !== doc.docGroup
    : pending.docType !== doc.docType || pending.docGroup !== doc.docGroup;

  const groupOptions =
    Array.isArray(docGroupOptions) && docGroupOptions.length > 0
      ? docGroupOptions
      : fileDocGroups;

  const handleSave = () => {
    if (!doc?._id || !isDirty) {
      setAnchor(null);
      return;
    }
    const data = isDocGroup
      ? [{ _id: doc._id, docGroup: pending.docGroup }]
      : [
          {
            _id: doc._id,
            docGroup: pending.docGroup,
            docType: pending.docType,
          },
        ];
    updateLoanDocMetadata({
      loanId,
      data,
      onSuccessFn: (d) => {
        dispatch(loanDrilldownSet(d));
        setAnchor(null);
      },
    });
  };

  return (
    <>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{
          cursor: "pointer",
          opacity: loading ? 0.6 : 1,
          pointerEvents: loading ? "none" : "auto",
          width: "100%",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Txt
          sx={{
            fontSize: "12px",
            color: isMissing ? INK4 : INK,
            fontStyle: isMissing ? "italic" : "normal",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            // No flex-grow — keep the text sized to its content so the
            // chevron sits directly to its right rather than being pushed to
            // the far edge of the cell. Still shrinks (+ ellipsis) when the
            // value is too long for the column.
            minWidth: 0,
          }}
        >
          {isMissing ? "Unassigned" : currentValue}
        </Txt>
        <KeyboardArrowDown sx={{ fontSize: 14, color: INK3, flexShrink: 0 }} />
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            border: `1px solid ${LINE}`,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            mt: 0.5,
            minWidth: 360,
            p: 1.25,
          },
        }}
      >
        <Flx column g={1.25}>
          {isDocGroup ? (
            <Autocomplete
              size="small"
              options={groupOptions}
              value={pending.docGroup || null}
              onChange={(e, val) =>
                setPending((p) => ({ ...p, docGroup: val || null }))
              }
              renderInput={(p) => (
                <TextField {...p} placeholder="Select document category…" />
              )}
            />
          ) : (
            <SelectFileAssignmentInput
              placeholder="Search categories and types…"
              docGroup={pending.docGroup}
              docType={pending.docType}
              size="small"
              onChange={(v) => setPending(v)}
            />
          )}
          <Flx ac jb g={1}>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                setPending({ docGroup: doc.docGroup, docType: doc.docType })
              }
              disabled={loading || !isDirty}
              sx={{ textTransform: "none", fontSize: "12px", color: INK3 }}
            >
              Reset
            </Button>
            <Flx ac g={1}>
              <Button
                size="small"
                variant="text"
                onClick={() => setAnchor(null)}
                disabled={loading}
                sx={{ textTransform: "none", fontSize: "12px", color: INK3 }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={loading || !isDirty}
                sx={{ textTransform: "none", fontSize: "12px" }}
              >
                {loading ? "Saving…" : "Save"}
              </Button>
            </Flx>
          </Flx>
        </Flx>
      </Popover>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   TABLE — filename (rename / preview) + inline meta column.
   `metaField` selects what the meta column edits:
     "docType"  → category + type picker (default)
     "docGroup" → category-only dropdown
   ═══════════════════════════════════════════════════════════ */

const InlineEditableLoanFilesTable = ({
  files,
  loanId,
  metaField = "docType",
  docGroupOptions,
  quickFilter,
  simpleTable = true,
  compactTable = false,
}) => {
  // Hidden documents never show in these lists. Clone each remaining file so
  // nothing here mutates the redux-backed loan-document objects. Re-clones
  // whenever the source changes (e.g. after a save round-trips through redux).
  const rowData = useMemo(
    () => (files || []).filter((f) => !isDocHidden(f)).map((f) => ({ ...f })),
    [files],
  );

  const [previewFile, setPreviewFile] = useState(null);

  const gridContext = useMemo(
    () => ({ loanId, metaField, docGroupOptions, onPreview: setPreviewFile }),
    [loanId, metaField, docGroupOptions],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "File Name",
        field: "file_display_name",
        flex: 1,
        minWidth: 180,
        editable: false,
        cellRenderer: FilenameCell,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          paddingLeft: "10px",
          paddingRight: "6px",
        },
      },
      {
        headerName: metaField === "docGroup" ? "Category" : "Type",
        field: metaField,
        flex: 1,
        minWidth: 120,
        width: 150,
        maxWidth: 220,
        editable: false,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        },
        cellRenderer: InlineMetaCell,
      },
      {
        headerName: "Uploaded",
        field: "uploadDate",
        width: 110,
        flex: 0,
        sort: "desc",
        editable: false,
        valueFormatter: (p) => formatDateTime(p.value, "M/d/yyyy") || "—",
        cellStyle: { display: "flex", alignItems: "center", color: INK3 },
      },
    ],
    [metaField],
  );

  return (
    <>
      <AgGridTableWrapper
        compactTable={compactTable}
        simpleTable={simpleTable}
        borderRadius={0}
        noBorder
        noBorderRadius
        // noBorderX
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          context={gridContext}
          getRowId={getRowId}
          quickFilterText={quickFilter}
          pagination={size(rowData) > 10}
          paginationPageSize={10}
          domLayout="autoHeight"
        />
      </AgGridTableWrapper>

      <LoanFilePreviewModal
        file={previewFile}
        open={!isNil(previewFile)}
        containerId={previewFile?.containerName}
        onClose={() => setPreviewFile(null)}
      />
    </>
  );
};

export default InlineEditableLoanFilesTable;
